import {
  Clock,
  HandHelping,
  ShieldCheck,
  Star,
  Store,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

import { getLocalizedPath, type RouteId } from '@/shared/routing/routePaths'
import type { Locale } from '@/shared/store/localeStore'

export type HelpAccentColor = 'red' | 'yellow' | 'green' | 'blue' | 'yellow-soft'

export type HelpCategoryKey =
  | 'how-it-works'
  | 'reliability'
  | 'cancel-game'
  | 'ratings'
  | 'waitlist'
  | 'stores'

export interface HelpCategory {
  /** ID lógico estable. NUNCA cambia. Es la fuente de verdad. */
  key: HelpCategoryKey
  /** Slugs en cada locale. Forman la URL final `/help/<slug>` o `/en/help/<slug>`. */
  slugs: { es: string; en: string }
  /**
   * Claves i18n LITERALES completas del título y subtítulo (nunca un prefijo
   * + sufijo construido: una poda de claves muertas no vería las construidas).
   */
  titleKey: string
  subtitleKey: string
  icon: LucideIcon
  accentColor: HelpAccentColor
  /** 2 categorías relacionadas para el bloque "Sigue explorando". */
  relatedKeys: [HelpCategoryKey, HelpCategoryKey]
}

export const helpCategories: HelpCategory[] = [
  {
    key: 'how-it-works',
    slugs: { es: 'como-funciona', en: 'how-it-works' },
    titleKey: 'help.categories.howItWorks.title',
    subtitleKey: 'help.categories.howItWorks.subtitle',
    icon: HandHelping,
    accentColor: 'yellow',
    relatedKeys: ['reliability', 'ratings'],
  },
  {
    key: 'reliability',
    slugs: { es: 'fiabilidad', en: 'reliability' },
    titleKey: 'help.categories.reliability.title',
    subtitleKey: 'help.categories.reliability.subtitle',
    icon: ShieldCheck,
    accentColor: 'green',
    relatedKeys: ['cancel-game', 'ratings'],
  },
  {
    key: 'cancel-game',
    slugs: { es: 'cancelar-partida', en: 'cancel-game' },
    titleKey: 'help.categories.cancellation.title',
    subtitleKey: 'help.categories.cancellation.subtitle',
    icon: XCircle,
    accentColor: 'red',
    relatedKeys: ['reliability', 'waitlist'],
  },
  {
    key: 'ratings',
    slugs: { es: 'valoraciones', en: 'ratings' },
    titleKey: 'help.categories.ratings.title',
    subtitleKey: 'help.categories.ratings.subtitle',
    icon: Star,
    accentColor: 'blue',
    relatedKeys: ['reliability', 'how-it-works'],
  },
  {
    key: 'waitlist',
    slugs: { es: 'lista-de-espera', en: 'waitlist' },
    titleKey: 'help.categories.waitlist.title',
    subtitleKey: 'help.categories.waitlist.subtitle',
    icon: Clock,
    accentColor: 'yellow-soft',
    relatedKeys: ['how-it-works', 'cancel-game'],
  },
  {
    key: 'stores',
    slugs: { es: 'tiendas', en: 'stores' },
    titleKey: 'help.categories.stores.title',
    subtitleKey: 'help.categories.stores.subtitle',
    icon: Store,
    accentColor: 'green',
    relatedKeys: ['how-it-works', 'cancel-game'],
  },
]

export function getHelpCategoryByKey(key: string): HelpCategory | undefined {
  return helpCategories.find((c) => c.key === key)
}

export function getHelpCategoryBySlug(slug: string): HelpCategory | undefined {
  return helpCategories.find((c) => c.slugs.es === slug || c.slugs.en === slug)
}

export function helpPath(key: string, locale: Locale): string {
  const cat = getHelpCategoryByKey(key)
  if (!cat) {
    if (typeof console !== 'undefined') {
      console.warn(`helpPath: unknown key "${key}" — falling back to /help`)
    }
    return getLocalizedPath('help-hub', locale)
  }
  return getLocalizedPath(`help-category:${cat.key}` as RouteId, locale)
}
